import { useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, query, where, getDocs } from "firebase/firestore";
import { storage, db } from "../../firebase";
import { getDefaultDepartmentValue } from "./mobileListUtils";

export function useMobileListHandlers({
  isTeacher,
  userProfile,
  setListType,
  selectedDepartment,
  departmentOptions,
  attendanceLogs,
  visitLogs,
  viewingItem,
  setViewingItem,
  formData,
  setFormData,
  teacherFormData,
  setTeacherFormData,
  setIsDetailEditing,
  setIsModalOpen,
  setIsUploading,
  onAdd,
  onUpdate,
  onDelete,
  onAddTeacher,
  onUpdateTeacher,
  onDeleteTeacher,
  openAlert,
  openConfirm,
}) {
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);

  const minSwipeDistance = 200;
  const maxVerticalDistance = 80;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const onListSwipeEnd = () => {
    if (!touchStart || !touchEnd || touchStartY == null || touchEndY == null) return;
    const horizontalDistance = touchStart - touchEnd;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    const isHorizontalIntent =
      Math.abs(horizontalDistance) > minSwipeDistance && verticalDistance < maxVerticalDistance;
    if (!isHorizontalIntent) return;
    const isLeftSwipe = horizontalDistance > 0;
    const isRightSwipe = horizontalDistance < 0;

    if (isLeftSwipe && !isTeacher && setListType) {
      setListType("teacher");
    } else if (isRightSwipe && isTeacher && setListType) {
      setListType("student");
    }
  };

  const onModalSwipeEnd = (closeHandler) => {
    if (!touchStart || !touchEnd || touchStartY == null || touchEndY == null) return;
    const horizontalDistance = touchStart - touchEnd;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    const isHorizontalIntent =
      Math.abs(horizontalDistance) > minSwipeDistance && verticalDistance < maxVerticalDistance;
    if (!isHorizontalIntent) return;
    const isRightSwipe = horizontalDistance < 0;

    if (isRightSwipe) {
      closeHandler();
    }
  };

  const fetchTeamMembers = async () => {
    if (!userProfile?.organizationId) return;
    setLinkLoading(true);
    try {
      const q = query(
        collection(db, "users"),
        where("organizationId", "==", userProfile.organizationId)
      );
      const snapshot = await getDocs(q);
      const members = snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
      setTeamMembers(members);
    } catch (error) {
      console.error("팀 멤버 조회 오류:", error);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleLinkAccount = async (memberUid, memberName) => {
    if (!viewingItem) return;
    try {
      await onUpdateTeacher({
        ...viewingItem,
        linkedUserId: memberUid,
        linkedUserName: memberName,
      });
      setShowLinkPopup(false);
      openAlert(
        `${viewingItem.name} 교사가 ${memberName}님의 계정과 연결되었습니다.`,
        "계정 연결 완료"
      );
    } catch (error) {
      console.error("계정 연결 오류:", error);
    }
  };

  const handleUnlinkAccount = async () => {
    if (!viewingItem) return;
    openConfirm(
      "계정 연결 해제",
      `${viewingItem.name} 교사의 계정 연결을 해제하시겠습니까?`,
      async () => {
        try {
          await onUpdateTeacher({
            ...viewingItem,
            linkedUserId: null,
            linkedUserName: null,
          });
          openAlert("계정 연결이 해제되었습니다.", "연결 해제 완료");
        } catch (error) {
          console.error("연결 해제 오류:", error);
        }
      }
    );
  };

  const openRegisterModal = () => {
    const defaultDept = getDefaultDepartmentValue({
      departmentOptions,
      selectedDepartment,
      userProfile,
    });

    if (isTeacher) {
      setTeacherFormData({
        name: "",
        gender: "남",
        phone: "",
        grade: defaultDept,
        birth: "",
        address: "",
        notes: "",
        role: "교사",
        charge: "",
        photoUrl: "",
      });
    } else {
      setFormData({
        name: "",
        gender: "남",
        phone: "",
        grade: defaultDept,
        class: "",
        school: "",
        guardian1Name: "",
        guardian1Phone: "",
        guardian2Name: "",
        guardian2Phone: "",
        birth: "",
        address: "",
        faithState: "기존신자",
        notes: "",
        photoUrl: "",
        prayerTopic: "",
      });
    }

    setIsModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const storageRef = ref(
        storage,
        `${isTeacher ? "teachers" : "students"}/${Date.now()}_${file.name}`
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      if (isTeacher) {
        setTeacherFormData((prev) => ({ ...prev, photoUrl: url }));
      } else {
        setFormData((prev) => ({ ...prev, photoUrl: url }));
      }
    } catch (error) {
      console.error("Upload failed", error);
      openAlert("이미지 업로드 실패", "오류");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isTeacher) {
      if (!teacherFormData.name) {
        openAlert(
          "이름이 비어 있어요! 소중한 동역자의 성함을 입력해 주시겠어요? ✨",
          "알림"
        );
        return;
      }
      onAddTeacher({ ...teacherFormData, id: Date.now() });
    } else {
      if (!formData.name) {
        openAlert(
          "이름은 꼭 필요해요! 우리 부서의 소중한 식구 이름을 알려주세요. ✨",
          "알림"
        );
        return;
      }
      onAdd(formData);
    }

    setIsModalOpen(false);
  };

  const handleDetailSave = (e) => {
    e.preventDefault();

    if (!viewingItem) return;

    const updatedData = isTeacher
      ? { ...teacherFormData, id: viewingItem.id }
      : { ...formData, id: viewingItem.id };

    if (isTeacher) {
      onUpdateTeacher(updatedData);
    } else {
      onUpdate(updatedData);
    }

    setIsDetailEditing(false);
    setViewingItem(updatedData);
  };

  const handleDelete = () => {
    if (!viewingItem) return;

    openConfirm(
      "사역 기록을 정리할까요?",
      "한 번 삭제하면 복구할 수 없습니다. 정말 보내드려야 할까요? 🥲",
      () => {
        if (isTeacher) {
          onDeleteTeacher(viewingItem.id);
        } else {
          onDelete(viewingItem.id);
        }
        setViewingItem(null);
      }
    );
  };

  const openDetail = (item) => {
    const myVisits = visitLogs
      .filter((log) => log && log.studentId === item.id && log.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    const lastVisitDate = myVisits.length > 0 ? myVisits[0].date : "-";

    const myAttendances = attendanceLogs
      .filter((log) => log && (log[item.id] === "present" || log[item.id] === "late"))
      .map((log) => log.date)
      .sort((a, b) => new Date(b) - new Date(a));
    const lastAttendanceDate = myAttendances.length > 0 ? myAttendances[0] : "-";

    const enrichedItem = {
      ...item,
      lastVisit: lastVisitDate,
      lastAttendance: lastAttendanceDate,
    };

    setViewingItem(enrichedItem);
    setIsDetailEditing(false);

    if (item.isTeacher || isTeacher) {
      setTeacherFormData(enrichedItem);
    } else {
      setFormData(enrichedItem);
    }
  };

  return {
    showLinkPopup,
    setShowLinkPopup,
    teamMembers,
    linkLoading,
    onTouchStart,
    onTouchMove,
    onListSwipeEnd,
    onModalSwipeEnd,
    fetchTeamMembers,
    handleLinkAccount,
    handleUnlinkAccount,
    openRegisterModal,
    handleImageUpload,
    handleSubmit,
    handleDetailSave,
    handleDelete,
    openDetail,
  };
}
